import { CurbLinksInterface } from "components/curb/map/deckgl/interfaces";
import { MeasurementSelectValue } from "components/curb/map/deckgl/MeasurementTool/DistanceTool";
import { RootObject } from "interfaces/Geoserver";
import { Map } from "mapbox-gl";

export const SET_MARKERS = "SET_MARKERS";
export const GET_CURB_LINKS = "GET_CURB_LINKS";
export const CURB_LINKS_RECEIVED = "CURB_LINKS_RECEIVED";
export const SET_MAP_OBJECT = "SET_MAP_OBJECT";
export const SET_LAT_LNG = "SET_LAT_LNG";
export const TOGGLE_MAP = "TOGGLE_MAP";
export const TOGGLE_DISTANCE_TOOL = "TOGGLE_DISTANCE_TOOL";
export const STORE_GEOSERVER_DATA = "STORE_GEOSERVER_DATA";
export const POST_FEEDBACK_DATA = "POST_FEEDBACK_DATA";
export const POST_SEGMENT_FEEDBACK_DATA = "POST_SEGMENT_FEEDBACK_DATA";
export const IS_POINTER_EVENTS_ENABLED = "IS_POINTER_EVENTS_ENABLED";
export const SET_HIGHLIGHTED_MAPLAYERS_DATA = "SET_HIGHLIGHTED_MAPLAYERS_DATA";

export enum LayersType {
    customActiveLayer = "custom-active-layer",
    customGeoserverLinksLayer = "custom-geoserver-links-layer",
    customHighlightBatchLinks = "custom-highlight-batch-links",
    customBatchLayer = "custom-batch-layer",
    customBatchViewLayer = "custom-batch-view-layer",
    customProposedLinksLayerForCustomSegment = "custom-proposed-links-layer-for-custom-segment",
    customPolygonHighlighted = "custom-polygon-highlighted-",
    customSegmentHighlighted = "custom-segment-highlighted-",
    hoveredSegmentToBeCreated = "hovered-segment-to-be-created",
    customCreateLayer = "custom-create-layer",
    customManagerActiveLayer = "custom-manager-active-layer",
    customCurbLinks = "custom-curb-links",
    customProposedLinks = "custom-proposed-links",
    customAssetSegmentLayer = "custom-asset-segment-layer",
    customFilteredProposedLinks = "custom-filter-proposed-links-layer",
    customFilteredLinks = "custom-filter-links-layer",
    //TODO: Highlight custo zone links
    customZoneLinkLayer = "custom-zone-links-layer"
}

export interface highlightedMapLayersDataType {
    [LayersType.customActiveLayer]: Array<any>;
    [LayersType.customGeoserverLinksLayer]: Array<any>;
    [LayersType.customHighlightBatchLinks]: Array<any>;
    [LayersType.customBatchLayer]: Array<any>;
    [LayersType.customBatchViewLayer]: Array<any>;
    [LayersType.customProposedLinksLayerForCustomSegment]: Array<any>;
    [LayersType.customPolygonHighlighted]: Array<any>;
    [LayersType.customSegmentHighlighted]: Array<any>;
    [LayersType.hoveredSegmentToBeCreated]: Array<any>;
    [LayersType.customCreateLayer]: Array<any>;
    [LayersType.customManagerActiveLayer]: Array<any>;
    [LayersType.customCurbLinks]: Array<any>;
    [LayersType.customProposedLinks]: Array<any>;
    [LayersType.customAssetSegmentLayer]: Array<any>;
    [LayersType.customFilteredProposedLinks]: Array<any>;
    [LayersType.customFilteredLinks]: Array<any>;
    [LayersType.customZoneLinkLayer]: Array<any>;
}

export interface GetCurbLinksParamsInterface {
    date: string;
    time: string;
    left_bottom_lat?: string;
    left_bottom_lng?: string;
    right_top_lat?: string;
    right_top_lng?: string;
}

export interface MapState {
    center: number[];
    zoomControl: boolean;
    mapBoxMaxZoom: number;
    mapBoxMinZoom: number;
    attributionControl: boolean;
    mapMarkers: SetMarkersInterface;
    curbLinks: CurbLinksInterface;
    mapObject: any;
    isCurbLinksReceived: boolean;
    isSatelliteMapSelected: boolean;
    isDistanceToolEnabled: boolean;
    distanceUnits: MeasurementSelectValue;
    geoserverData: RootObject;
    isPointerEventsEnabled: boolean;
    highlightedMapLayersData: highlightedMapLayersDataType;
}
export const defaultDistanceUnits = {
    displayUnits: "m",
    label: "Meters",
    value: "meters"
};

export const defaultCurbLinks = {
    links: [],
    regulations: []
};

export interface PostFeedbackDataInterface {
    description: string;
    title: string;
    email: string;
    latitude: number | null;
    longitude: number | null;
    geostatus: string;
}
export interface PostSegmentFeedbackDataInterface {
    description: string;
    name: string;
    email: string;
    link: number;
    restriction: number;
    response_category: number[];
    responder_type: number;
}

export interface SetMarkersInterface {
    [param: string]: {};
}

export interface SetMapObject {
    type: typeof SET_MAP_OBJECT;
    payload: { obj: Map };
}

export interface SetMarkers {
    type: typeof SET_MARKERS;
    payload: { markers: SetMarkersInterface };
}

export interface GetCurbLinks {
    type: typeof GET_CURB_LINKS;
    payload: { params: GetCurbLinksParamsInterface; nextProcess?: () => void };
}

export interface CurbLinksReceived {
    type: typeof CURB_LINKS_RECEIVED;
    payload: { curbLinks: CurbLinksInterface };
}

export interface SetLatLng {
    type: typeof SET_LAT_LNG;
    payload: { center: number[] };
}

export interface ToggleMap {
    type: typeof TOGGLE_MAP;
    payload: { mapType: boolean };
}

export interface ToggleDistanceTool {
    type: typeof TOGGLE_DISTANCE_TOOL;
    payload: { toggle: boolean; units: MeasurementSelectValue };
}

export interface StoreGeoserverData {
    type: typeof STORE_GEOSERVER_DATA;
    payload: { data: RootObject };
}

export interface PostFeedbackData {
    type: typeof POST_FEEDBACK_DATA;
    payload: {
        data: PostFeedbackDataInterface;
        contactSupport?: boolean;
        afterProcess?: () => void;
    };
}

export interface PostSegmentFeedbackData {
    type: typeof POST_SEGMENT_FEEDBACK_DATA;
    payload: {
        data: PostSegmentFeedbackDataInterface;
        afterProcess?: () => void;
    };
}

export interface IsPointerEventsEnabled {
    type: typeof IS_POINTER_EVENTS_ENABLED;
    payload: { data: boolean };
}

export interface SetHighlightedMapLayersData {
    type: typeof SET_HIGHLIGHTED_MAPLAYERS_DATA;
    payload: { highlightedMapLayersData: highlightedMapLayersDataType };
}

export const defaultHighlightedMapLayersData = {
    [LayersType.customActiveLayer]: [],
    [LayersType.customGeoserverLinksLayer]: [],
    [LayersType.customHighlightBatchLinks]: [],
    [LayersType.customBatchLayer]: [],
    [LayersType.customBatchViewLayer]: [],
    [LayersType.customProposedLinksLayerForCustomSegment]: [],
    [LayersType.customPolygonHighlighted]: [],
    [LayersType.customSegmentHighlighted]: [],
    [LayersType.hoveredSegmentToBeCreated]: [],
    [LayersType.customCreateLayer]: [],
    [LayersType.customManagerActiveLayer]: [],
    [LayersType.customCurbLinks]: [],
    [LayersType.customProposedLinks]: [],
    [LayersType.customAssetSegmentLayer]: [],
    [LayersType.customFilteredProposedLinks]: [],
    [LayersType.customFilteredLinks]: [],
    [LayersType.customZoneLinkLayer]: []
};

export type MapTypes =
    | SetMapObject
    | SetMarkers
    | GetCurbLinks
    | CurbLinksReceived
    | SetLatLng
    | ToggleMap
    | ToggleDistanceTool
    | StoreGeoserverData
    | PostFeedbackData
    | PostSegmentFeedbackData
    | IsPointerEventsEnabled
    | SetHighlightedMapLayersData;
